import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Building2, Shield, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';

const Hero: React.FC = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <section className="relative overflow-hidden py-20 md:py-32">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-purple-50" />
      
      <div className="relative container mx-auto px-4">
        <motion.div 
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="text-center"
        >
          <motion.div variants={fadeInUp} className="mb-6">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Shield className="h-4 w-4" />
              Plateforme sécurisée pour les établissements
            </div>
          </motion.div>

          <motion.h1 
            variants={fadeInUp}
            className="text-4xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight"
          >
            Gérez vos <span className="text-blue-600">établissements</span> et 
            <span className="text-blue-600"> finances</span> en toute simplicité
          </motion.h1>
          
          <motion.p 
            variants={fadeInUp}
            className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto"
          >
            EXELIUS est la solution tout-en-un pour les superviseurs qui veulent 
            gérer efficacement plusieurs établissements, leurs secrétaires et 
            leurs flux financiers depuis une seule plateforme.
          </motion.p>
          
          <motion.div 
            variants={fadeInUp}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-16"
          >
            <Link 
              to="/register" 
              className="px-8 py-4 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all duration-300 hover:shadow-xl inline-flex items-center justify-center text-lg"
            >
              Commencer gratuitement
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link 
              to="/login" 
              className="px-8 py-4 border-2 border-blue-600 text-blue-600 rounded-xl font-semibold hover:bg-blue-50 transition-all duration-300 inline-flex items-center justify-center text-lg"
            >
              Accéder à mon compte
            </Link>
          </motion.div>

          {/* Stats */}
          <motion.div 
            variants={fadeInUp}
            className="grid grid-cols-2 md:grid-cols-3 gap-6 max-w-3xl mx-auto"
          >
            <div className="text-center p-4">
              <div className="text-3xl font-bold text-blue-600 mb-2">500+</div>
              <div className="text-gray-600">Établissements</div>
            </div>
            <div className="text-center p-4">
              <div className="text-3xl font-bold text-blue-600 mb-2">1,200+</div>
              <div className="text-gray-600">Utilisateurs</div>
            </div>
            <div className="text-center p-4">
              <div className="text-3xl font-bold text-blue-600 mb-2">€2M+</div>
              <div className="text-gray-600">Transactions gérées</div>
            </div>
          </motion.div>

          {/* Hero Image/Illustration */}
          <motion.div 
            variants={fadeInUp}
            className="mt-16 relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-1">
                <div className="bg-white p-8 md:p-12">
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <Building2 className="h-8 w-8 text-blue-600" />
                        <span className="font-semibold">Superviseur</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        Créez des établissements, gérez les rôles et visualisez les rapports
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <BarChart3 className="h-8 w-8 text-green-600" />
                        <span className="font-semibold">Secrétaire</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        Enregistrez les transactions, gévez les preuves et demandez des retraits
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <Shield className="h-8 w-8 text-purple-600" />
                        <span className="font-semibold">Sécurité</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        Données chiffrées, authentification forte et audits complets
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Floating elements */}
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-blue-100 rounded-full opacity-50" />
            <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-purple-100 rounded-full opacity-50" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;